const safeJsonParse = (raw, fallback = null) => {
    try {
        return JSON.parse(raw);
    } catch (_e) {
        return fallback;
    }
};

const getStorage = () => window?.localStorage || null;

export const createIndexedLocalStore = ({
    keyPrefix,
    indexKey,
    defaultMaxAgeMs = 4 * 60 * 60 * 1000,
    defaultMaxEntries = 10,
    composeKey,
    createEntry,
}) => {
    const getKey = (...keyParts) => `${keyPrefix}${composeKey(...keyParts)}`;

    const readIndex = () => {
        const storage = getStorage();
        if (!storage) return [];
        const parsed = safeJsonParse(storage.getItem(indexKey), []);
        return Array.isArray(parsed) ? parsed : [];
    };

    const writeIndex = (index) => {
        const storage = getStorage();
        if (!storage) return;

        try {
            storage.setItem(indexKey, JSON.stringify(index));
        } catch (_e) {
        }
    };

    const rebuildIndexFromStorage = () => {
        const storage = getStorage();
        if (!storage) return [];

        return Object.keys(storage)
            .filter(key => key.startsWith(keyPrefix))
            .map(key => {
                const entry = safeJsonParse(storage.getItem(key));
                return {
                    key,
                    savedAt: typeof entry?.savedAt === 'number' ? entry.savedAt : Date.now(),
                };
            });
    };

    const upsertIndex = (key, savedAt) => {
        const index = readIndex();
        const base = index.length === 0 ? rebuildIndexFromStorage() : index;
        const deduped = new Map();
        [{ key, savedAt }, ...base].forEach((row) => {
            if (!deduped.has(row.key)) {
                deduped.set(row.key, row);
            }
        });

        writeIndex(Array.from(deduped.values()));
    };

    const removeFromIndex = (key) => {
        const index = readIndex();
        writeIndex(index.filter(row => row.key !== key));
    };

    const read = (...keyParts) => {
        const storage = getStorage();
        if (!storage) return null;
        const key = getKey(...keyParts);
        const entry = safeJsonParse(storage.getItem(key));
        return entry && typeof entry === 'object' ? entry : null;
    };

    const remove = (...keyParts) => {
        const storage = getStorage();
        if (!storage) return;
        const key = getKey(...keyParts);
        try {
            storage.removeItem(key);
            removeFromIndex(key);
        } catch (_e) {
        }
    };

    const prune = ({
        maxAgeMs = defaultMaxAgeMs,
        maxEntries = defaultMaxEntries,
    } = {}) => {
        const storage = getStorage();
        if (!storage) return;

        const currentIndex = readIndex();
        const index = currentIndex.length > 0 ? currentIndex : rebuildIndexFromStorage();
        const now = Date.now();
        const aliveKeys = new Set(
            Object.keys(storage).filter(key => key.startsWith(keyPrefix))
        );

        const removeItem = (key) => {
            try {
                storage.removeItem(key);
            } catch (_e) {
            }
        };

        const present = index.filter(row => aliveKeys.has(row.key));
        const fresh = present.filter((row) => {
            if (now - row.savedAt > maxAgeMs) {
                removeItem(row.key);
                return false;
            }
            return true;
        });

        const sorted = fresh.sort((a, b) => b.savedAt - a.savedAt);
        sorted.slice(maxEntries).forEach(row => removeItem(row.key));
        const surviving = sorted.slice(0, maxEntries);

        const survivingKeys = new Set(surviving.map(row => row.key));
        aliveKeys.forEach((key) => {
            if (!survivingKeys.has(key)) {
                removeItem(key);
            }
        });

        writeIndex(surviving);
    };

    const write = (...args) => {
        const storage = getStorage();
        if (!storage) return;

        const keyParts = args.slice(0, -1);
        const data = args[args.length - 1];
        const key = getKey(...keyParts);
        const savedAt = Date.now();
        const entry = createEntry({ keyParts, data, savedAt });

        const persist = () => {
            storage.setItem(key, JSON.stringify(entry));
            upsertIndex(key, savedAt);
        };

        try {
            persist();
            prune();
        } catch (_e) {
            try {
                prune({ maxEntries: 1 });
                persist();
            } catch (_ignored) {
            }
        }
    };

    return {
        getKey,
        read,
        write,
        remove,
        prune,
    };
};
